import { useState } from 'react'
import {
  Link as RouterLink,
  Navigate,
  useLocation,
  useNavigate,
} from 'react-router-dom'
import { LoadingButton } from '@mui/lab'
import { Link } from '@mui/material'
import { ApiError } from '@/api/http-client'
import { useRecoverPassword } from '@/api/user-manager'
import { removeNonNumeric, toast } from '@/helpers'
import { routes } from '@/router'
import { sessionButtonSx } from '../session-button'
import { RedirectIfAuthenticated, SessionScreen } from '../session-screen'
import { flowProgress } from '../profile/profile-steps'
import { useSignupDraft } from './use-signup-draft'

function maskedEmailFromState(state: unknown): string | null {
  if (!state || typeof state !== 'object' || !('maskedEmail' in state)) {
    return null
  }
  const value = state.maskedEmail
  return typeof value === 'string' && value ? value : null
}

export function SignupCpfTakenPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { draft } = useSignupDraft()
  const [sentTo, setSentTo] = useState<string | null>(null)
  const maskedEmail = maskedEmailFromState(location.state)
  const shownEmail = sentTo ?? maskedEmail

  const { mutateAsync: recoverPassword, isPending } = useRecoverPassword()

  if (!draft.taxId.trim()) {
    return <Navigate to={routes.session.signup.cpf} replace />
  }

  async function handleRecover() {
    try {
      const result = await recoverPassword({
        tax_id: removeNonNumeric(draft.taxId),
      })
      setSentTo(result.masked_email)
    } catch (error) {
      if (error instanceof ApiError) return
      toast.error('Não foi possível enviar o e-mail de recuperação')
    }
  }

  return (
    <RedirectIfAuthenticated>
      <SessionScreen
        title={sentTo ? 'E-mail enviado' : 'Já existe uma conta com este CPF'}
        subtitle={
          shownEmail
            ? sentTo
              ? `Enviamos um link de recuperação para ${shownEmail}.`
              : `O e-mail cadastrado é ${shownEmail}.`
            : 'Recupere a senha para entrar com o e-mail cadastrado.'
        }
        progress={flowProgress('cpf', null)}
        onBack={() => navigate(routes.session.signup.cpf)}
        footer={
          sentTo ? (
            <LoadingButton
              size="large"
              fullWidth
              onClick={() => navigate(routes.session.signin)}
              sx={sessionButtonSx()}
            >
              Fazer login
            </LoadingButton>
          ) : (
            <LoadingButton
              size="large"
              fullWidth
              loading={isPending}
              onClick={handleRecover}
              sx={sessionButtonSx()}
            >
              Recuperar senha
            </LoadingButton>
          )
        }
      >
        {!sentTo && (
          <Link
            component={RouterLink}
            to={routes.session.signin}
            sx={{ alignSelf: 'center' }}
          >
            Fazer login
          </Link>
        )}
      </SessionScreen>
    </RedirectIfAuthenticated>
  )
}
